import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, CalendarDays, Check, CheckSquare, PackagePlus, Search, Target, Users } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api'
import { Button, EmptyState, Field, Modal, Status } from '../components'
import type { Batch, DriverUser } from '../types'

type AvailableOrder = {
  id: string
  recipientName: string
  cep: string
  street: string
  streetNumber: string
  neighborhood: string
  city: string
  status: string
  geocodeStatus: string
  createdAt: string
}

function normalizeSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim()
}

export function BatchesPage() {
  const [batches, setBatches] = useState<Batch[]>([])
  const [availableOrders, setAvailableOrders] = useState<AvailableOrder[]>([])
  const [activeDriverCount, setActiveDriverCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [search, setSearch] = useState('')
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([])
  const [plannedDriverCount, setPlannedDriverCount] = useState(1)
  const [targetOrdersPerDriver, setTargetOrdersPerDriver] = useState(40)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const load = async () => {
    const [batchResult, availableResult, userResult] = await Promise.all([
      api<{ batches: Batch[] }>('/api/batches'),
      api<{ orders: AvailableOrder[] }>('/api/orders/available'),
      api<{ users: DriverUser[] }>('/api/users'),
    ])
    setBatches(batchResult.batches)
    setAvailableOrders(availableResult.orders)
    const count = userResult.users.filter((user) => user.role === 'DRIVER' && Number(user.isActive) === 1 && user.hasPhoto).length
    setActiveDriverCount(count)
    setPlannedDriverCount((current) => Math.min(Math.max(1, current), Math.max(1, count)))
  }
  useEffect(() => { void load() }, [])

  const filteredOrders = useMemo(() => {
    const term = normalizeSearch(search)
    if (!term) return availableOrders
    const tokens = term.split(/\s+/).filter(Boolean)
    const digits = search.replace(/\D/g, '')
    return availableOrders.filter((order) => {
      const text = normalizeSearch(`${order.neighborhood} ${order.city} ${order.street} ${order.recipientName}`)
      return tokens.every((token) => text.includes(token)) || Boolean(digits && order.cep.replace(/\D/g, '').includes(digits))
    })
  }, [availableOrders, search])

  const capacity = plannedDriverCount * targetOrdersPerDriver
  const overCapacity = selectedOrderIds.length > capacity

  const openCreator = () => {
    setName('')
    setDeliveryDate('')
    setSearch('')
    setSelectedOrderIds([])
    setTargetOrdersPerDriver(40)
    setPlannedDriverCount(Math.max(1, activeDriverCount))
    setError('')
    setOpen(true)
  }

  const toggleOrder = (orderId: string) => {
    setSelectedOrderIds((current) => current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId])
  }
  const selectAllResults = () => {
    setSelectedOrderIds((current) => [...new Set([...current, ...filteredOrders.map((order) => order.id)])])
  }
  const selectToCapacity = () => {
    const remaining = Math.max(0, capacity - selectedOrderIds.length)
    const current = new Set(selectedOrderIds)
    const candidates = filteredOrders.filter((order) => !current.has(order.id)).slice(0, remaining).map((order) => order.id)
    setSelectedOrderIds((selection) => [...selection, ...candidates])
  }

  const create = async () => {
    setSaving(true)
    setError('')
    try {
      const result = await api<{ id: string }>('/api/batches', {
        method: 'POST',
        body: JSON.stringify({ name, plannedDeliveryDate: deliveryDate || undefined, orderIds: selectedOrderIds }),
      })
      navigate(`/remessas/${result.id}`)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Não foi possível criar a remessa')
    } finally { setSaving(false) }
  }

  return (
    <div className="page">
      <header className="page-header"><div><p className="eyebrow">Planejamento semanal</p><h1>Remessas e rotas</h1><p>Escolha o recorte de pedidos antes de formar e distribuir uma remessa.</p></div><Button onClick={openCreator} disabled={!availableOrders.length}><PackagePlus size={16} /> Nova remessa</Button></header>
      <div className="notice notice-neutral"><strong>{availableOrders.length.toLocaleString('pt-BR')}</strong> pedidos estão na fila e continuam disponíveis até serem selecionados para uma remessa.</div>
      <section className="batch-list">
        {batches.map((batch) => (
          <Link to={`/remessas/${batch.id}`} key={batch.id} className="batch-row">
            <div><strong>{batch.name}</strong><span><CalendarDays size={14} /> {batch.plannedDeliveryDate ? new Date(`${batch.plannedDeliveryDate}T12:00:00`).toLocaleDateString('pt-BR') : 'Data ainda não definida'}</span></div>
            <div className="batch-numbers"><span><strong>{Number(batch.orderCount).toLocaleString('pt-BR')}</strong> pedidos</span><span><strong>{Number(batch.routeCount)}</strong> rotas</span></div>
            <Status value={batch.status} /><ArrowRight size={18} />
          </Link>
        ))}
        {!batches.length ? <EmptyState title="Nenhuma remessa criada" description="Selecione somente os pedidos que realmente serão distribuídos na próxima operação." /> : null}
      </section>
      {open ? <Modal title="Criar nova remessa" size="wide" onClose={() => !saving && setOpen(false)}>
        <div className="modal-body batch-create-body">
          <div className="batch-create-fields">
            <Field label="Nome da remessa" placeholder="Ex: Entregas de 10 a 16 de agosto" value={name} onChange={(event) => setName(event.target.value)} />
            <Field label="Data prevista (opcional)" type="date" value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} />
          </div>
          <section className="batch-capacity">
            <div><Users size={17} /><label><span>Entregadores previstos</span><input aria-label="Entregadores previstos" type="number" min={1} max={Math.max(1, activeDriverCount)} value={plannedDriverCount} onChange={(event) => setPlannedDriverCount(Math.max(1, Math.min(Math.max(1, activeDriverCount), Number(event.target.value) || 1)))} /></label></div>
            <div><Target size={17} /><label><span>Máximo por entregador</span><input aria-label="Máximo de pedidos por entregador" type="number" min={1} max={500} value={targetOrdersPerDriver} onChange={(event) => setTargetOrdersPerDriver(Math.max(1, Math.min(500, Number(event.target.value) || 1)))} /></label></div>
            <strong>Capacidade: {capacity.toLocaleString('pt-BR')} pedidos</strong>
          </section>
          <label className="route-search batch-create-search"><Search size={17} /><input aria-label="Buscar pedidos disponíveis por bairro ou CEP" placeholder="Buscar bairro ou CEP, ex.: Taquara ou 22710" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <div className="batch-selection-summary">
            <div><strong>{selectedOrderIds.length.toLocaleString('pt-BR')} selecionados</strong><span>{filteredOrders.length.toLocaleString('pt-BR')} resultados · {availableOrders.length.toLocaleString('pt-BR')} disponíveis</span></div>
            <div><Button variant="secondary" onClick={selectToCapacity} disabled={selectedOrderIds.length >= capacity || !filteredOrders.length}><Target size={14} /> Selecionar até a capacidade</Button><Button variant="secondary" onClick={selectAllResults} disabled={!filteredOrders.length}><CheckSquare size={14} /> Selecionar todos os resultados</Button><Button variant="ghost" onClick={() => setSelectedOrderIds([])} disabled={!selectedOrderIds.length}>Limpar</Button></div>
          </div>
          <div className="batch-create-order-list">
            {filteredOrders.map((order) => <label key={order.id}>
              <input type="checkbox" checked={selectedOrderIds.includes(order.id)} onChange={() => toggleOrder(order.id)} />
              <span><strong>{order.neighborhood} · CEP {order.cep}</strong><small>{order.recipientName} · {order.street}, {order.streetNumber} · {order.city}</small></span>
              <Status value={order.geocodeStatus} /><Check size={15} />
            </label>)}
            {!filteredOrders.length ? <EmptyState title="Nenhum pedido disponível" description="Tente outro bairro ou informe parte do CEP." /> : null}
          </div>
          {overCapacity ? <div className="form-alert">A seleção ultrapassa a capacidade em {(selectedOrderIds.length - capacity).toLocaleString('pt-BR')} pedidos. Aumente a quantidade de entregadores, a meta ou reduza a seleção.</div> : null}
          {!activeDriverCount ? <div className="form-alert">Cadastre ao menos um entregador ativo antes de formar a remessa.</div> : null}
          {error ? <div className="form-error">{error}</div> : null}
        </div>
        <footer className="modal-footer"><Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button><Button onClick={() => void create()} loading={saving} disabled={name.trim().length < 2 || !selectedOrderIds.length || overCapacity || !activeDriverCount}>Criar remessa com {selectedOrderIds.length.toLocaleString('pt-BR')} pedidos</Button></footer>
      </Modal> : null}
    </div>
  )
}
